import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import React, { useCallback, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { WorkspaceRoutinesStackScreenProps } from "../WorkspaceRoutinesNavigation";
import { FlatList } from "react-native";
import { AHIcon, AHSeparator, AHText, Box, ListItem } from "arkh";
import { useOrgListStyleProps } from "../../useOrgHomeDetailContainerStyle";
import { keyExtractorId } from "@/src/features/Storecast/Storecast/WidgetAdd/utils/keyExtractors";
import { WorkspaceRoutineTemplateCreatedRoutinesItemRowContainer } from "./WorkspaceRoutineTemplateCreatedRoutinesItemRowContainer";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { WorkspaceEmptyListItem } from "../../WorkspaceEmptyListItem";
import { WorkspaceDetailNavigationHeader } from "../../WorkspaceDetailNavigationHeader";
import { HeaderButton } from "@/src/components/mobile/Button/HeaderButtons";
import { WorkspaceDetailHeader } from "../../WorkspaceDetailHeader";
import { useWorkspaceId } from "../../workspaceId";
import { OrgSearchInput } from "../../OrgSearchInput";
import { SortableColumnTitle } from "../../SortableColumnTitle";
import { PillIndicator } from "../../PillIndicator";
import { WorkspaceRoutineOccasionDescription } from "../WorkspaceRoutineOccasion/WorkspaceRoutineOccasionDescription";
import { WorkspaceRoutineTemplateDetailQuery } from "./__generated__/WorkspaceRoutineTemplateDetailQuery.graphql";
type Props =
  WorkspaceRoutinesStackScreenProps<"WorkspaceRoutineTemplateDetail">;

function InnerWorkspaceRoutineTemplateDetail(props: Props & { fetchKey: string }) {
  const workspaceId = useWorkspaceId();
  const intl_tokens = useIntlTokens();
  const listStyle = useOrgListStyleProps();
  const [search, setSearch] = useState("");
  const [order, setOrder] = useState<"ASC" | "DESC">("DESC");
  const [orderBy, setOrderBy] = useState<"name" | "numOfRoutines" | "status">(
    "numOfRoutines"
  );

  const data = useLazyLoadQuery<WorkspaceRoutineTemplateDetailQuery>(
    graphql`
      query WorkspaceRoutineTemplateDetailQuery(
        $routineId: ID!
        $workspaceId: ID!
      ) {
        workspace(id: $workspaceId) {
          id
          stores {
            id
            name
            routinesFromTemplate(templateId: $routineId) {
              id
              title
              currentOccasion {
                ...RoutineOccasionStatusCircle_RoutineOccasion
                sequenceNumber
                status(
                  calculateNoTasks: true
                  calculateNotCompletedStarted: true
                )
              }
            }
          }
        }
        routine(id: $routineId) {
          id
          title
          ...WorkspaceRoutineOccasionDescription_routine
        }
      }
    `,
    {
      routineId: props.route.params.routineId,
      workspaceId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const listData = useMemo(() => {
    return [
      { type: "header" as const, id: "list-header" },
      ...(data?.workspace?.stores
        .filter((store) =>
          store.name.toLocaleLowerCase().includes(search.toLocaleLowerCase())
        )
        .flatMap((store) => {
          return [
            {
              type: "item" as const,
              id: store.id,
              title: store.name,
              numOfRoutines: store.routinesFromTemplate.length,
              status:
                store.routinesFromTemplate.length === 0
                  ? -1
                  : store.routinesFromTemplate.reduce((acc, cur) => {
                      if (
                        [
                          "COMPLETED",
                          "REQUIRED_COMPLETED",
                          "NO_TASKS_COMPLETED",
                        ].includes(cur.currentOccasion?.status ?? "")
                      ) {
                        return acc + 100;
                      }
                      return acc;
                    }, 0) / store.routinesFromTemplate.length,
            },
          ];
        })
        .sort((a, b) => {
          switch (orderBy) {
            case "name":
              return order === "DESC"
                ? a.title.localeCompare(b.title)
                : b.title.localeCompare(a.title);
            case "numOfRoutines":
              return order === "DESC"
                ? b.numOfRoutines - a.numOfRoutines
                : a.numOfRoutines - b.numOfRoutines;
            case "status":
              return order === "DESC"
                ? a.status - b.status
                : b.status - a.status;
          }
        }) ?? []),
    ];
  }, [data?.workspace?.stores, orderBy, search, order]);

  const ListHeaderComponent = useMemo(() => {
    return (
      <Box color="surface-background-light">
        <OrgSearchInput value={search} onChangeText={setSearch} />
        <ListItem disabled border="border-primary" displayMode="TABLE">
          <WorkspaceRoutineTemplateCreatedRoutinesItemRowContainer
            title={
              <SortableColumnTitle
                title={intl_tokens.Name}
                onOrderBySelect={() => {
                  setOrderBy("name");
                }}
                order={orderBy === "name" ? order : null}
                onOrderChange={setOrder}
              />
            }
            status={
              <SortableColumnTitle
                title={intl_tokens.Status}
                onOrderBySelect={() => {
                  setOrderBy("status");
                }}
                onOrderChange={setOrder}
                order={orderBy === "status" ? order : null}
              />
            }
            numOfRoutines={
              <SortableColumnTitle
                title={intl_tokens.Routines}
                onOrderBySelect={() => {
                  setOrderBy("numOfRoutines");
                }}
                onOrderChange={setOrder}
                order={orderBy === "numOfRoutines" ? order : null}
              />
            }
          />
        </ListItem>
      </Box>
    );
  }, [
    search,
    intl_tokens.Name,
    intl_tokens.Status,
    intl_tokens.Routines,
    orderBy,
    order,
  ]);

  const DescriptionHeader = useMemo(() => {
    if (!data?.routine) return <Box />;
    return (
      <Box color="surface-background-light">
        <WorkspaceRoutineOccasionDescription routine={data.routine} />
      </Box>
    );
  }, [data?.routine]);

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      if (item.type === "header") {
        return ListHeaderComponent;
      }
      return (
        <ListItem
          displayMode="TABLE"
          border="border-primary"
          isLast={index === listData.length - 1}
          isFirst={false}
          onPress={() => {
            props.navigation.navigateDeprecated(
              "WorkspaceRoutineTemplateStoreDetail",
              {
                routineId: props.route.params.routineId,
                storeId: item.id,
              }
            );
          }}
        >
          <WorkspaceRoutineTemplateCreatedRoutinesItemRowContainer
            title={
              <Box style={{ flexDirection: "row", alignItems: "center" }}>
                <AHIcon type="store" size="small" color="text-secondary" />
                <AHText
                  spaceLeft="small"
                  type="subtitle"
                  color={
                    item.numOfRoutines > 0 ? "text-primary" : "text-secondary"
                  }
                >
                  {item.title}
                </AHText>
              </Box>
            }
            numOfRoutines={
              <AHText
                type="subtitle"
                color={
                  item.numOfRoutines > 0 ? "text-primary" : "text-secondary"
                }
              >
                {item.numOfRoutines}
              </AHText>
            }
            status={
              item.numOfRoutines > 0 ? (
                <PillIndicator percentage={item.status} />
              ) : null
            }
          />
        </ListItem>
      );
    },
    [
      ListHeaderComponent,
      listData.length,
      props.navigation,
      props.route.params.routineId,
    ]
  );

  return (
    <>
      <WorkspaceDetailNavigationHeader
        left={
          <HeaderButton
            type="WEB-BACK"
            customLabel={intl_tokens.RoutineLibrary}
            onPress={() => {
              props.navigation.goBack();
            }}
          />
        }
        right={null}
      />
      <Box
        spaceHorizontal="medium"
        style={{
          maxWidth: 900,
          width: "100%",
          alignSelf: "center",
        }}
        spaceTop="medium"
      >
        <WorkspaceDetailHeader
          color="surface-background-light"
          spaceBottom="none"
          spaceTop="none"
          title={data?.routine?.title}
        />
      </Box>
      <FlatList
        {...listStyle}
        data={listData}
        ItemSeparatorComponent={AHSeparator}
        ListHeaderComponent={DescriptionHeader}
        ListEmptyComponent={<WorkspaceEmptyListItem />}
        renderItem={renderItem}
        keyExtractor={keyExtractorId}
        stickyHeaderIndices={[1]}
      />
    </>
  );
}

export function WorkspaceRoutineTemplateDetail(props: Props) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <InnerWorkspaceRoutineTemplateDetail fetchKey={fetchKey} {...props} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}
